import {SyntheticEvent, useMemo, useRef, useState} from "react";
import {handleError} from "utils/error";

const objKeys = <T extends Record<string, any>>(obj: T) =>
  Object.keys(obj) as Array<keyof T>;

export type FieldValidationError = string | undefined;

// Empty string is considered an error
const isNotError = (error: FieldValidationError) => error !== "" && !error;

export type FieldValidator<T> = (value: T) => FieldValidationError;

type OnChangeHandler<V> = (value: V) => void;

type ValueState<T> = {
  value: T;
  error: FieldValidationError;
};

// [DefaultValue, Validator]
type FieldConfig<T> = [T, FieldValidator<T>];

type Value<T> = T extends FieldConfig<infer U> ? U : never;

type ActionMap<T extends Record<string, FieldConfig<any>>> = {
  [K in keyof T]: OnChangeHandler<Value<T[K]>>;
};

type ValueStateMap<T extends Record<string, FieldConfig<any>>> = {
  [K in keyof T]: ValueState<Value<T[K]>>;
};

type ValueMap<T extends Record<string, FieldConfig<any>>> = {
  [K in keyof T]: Value<T[K]>;
};

export type FormField<T> = {
  value: T;
  error: FieldValidationError;
  onChange: OnChangeHandler<T>;
};

type FieldMap<T extends Record<string, FieldConfig<any>>> = {
  [K in keyof T]: FormField<Value<T[K]>>;
};

type UseFormOptions<T extends Record<string, FieldConfig<any>>> = {
  fields: T;
  onSubmit: (data: ValueMap<T>) => Promise<void>;
};

type UseFormResult<T extends Record<string, FieldConfig<any>>> = {
  handleSubmit: (event?: SyntheticEvent<any>) => void;
  fields: FieldMap<T>;
  submitting: boolean;
  resetFields: (newFields: { [K in keyof T]: Value<T[K]> }) => void;
};

const useForm = <T extends Record<string, FieldConfig<any>>>(
  options: UseFormOptions<T>,
): UseFormResult<T> => {
  const defaults = useRef<T>(options.fields);

  const [values, setValues] = useState(() => {
    const initialValues = {} as ValueStateMap<T>;
    for (const key of objKeys(defaults.current)) {
      const [value, validator] = defaults.current[key];
      initialValues[key] = {
        value: value as Value<T[keyof T]>,
        error: validator(value),
      };
    }
    return initialValues;
  });

  const isValid = objKeys(values).every(key => isNotError(values[key]?.error));

  const updateActions = useMemo(() => {
    const actions = {} as ActionMap<T>;
    for (const key of objKeys(defaults.current)) {
      const validator = defaults.current[key][1];
      actions[key] = (value: Value<T[keyof T]>) => {
        setValues(prev => ({
          ...prev,
          [key]: {value, error: validator(value)},
        }));
      };
    }
    return actions;
  }, []);

  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (event?: SyntheticEvent<any>) => {
    event?.preventDefault();
    event?.stopPropagation();

    if (!isValid) return setShowErrors(true);

    const data = {} as ValueMap<T>;
    for (const key of objKeys(values)) data[key] = values[key].value;

    setSubmitting(true);
    options
      .onSubmit(data)
      .catch(err => handleError(err))
      .finally(() => setSubmitting(false));
  };

  const resetFields = (newFields: { [K in keyof T]: Value<T[K]> }) => {
    setValues(prev => {
      const newValues = {...prev};
      for (const key of objKeys(prev)) {
        const validator = defaults.current[key][1];
        newValues[key] = {
          value: newFields[key],
          error: validator(newFields[key]),
        };
      }
      return newValues;
    });
    setShowErrors(false);
  };

  const fields = {} as FieldMap<T>;
  for (const key of objKeys(values)) {
    fields[key] = {
      value: values[key].value,
      error: showErrors ? values[key].error : undefined,
      onChange: updateActions[key],
    };
  }

  return {
    handleSubmit,
    fields,
    submitting,
    resetFields,
  };
};

export default useForm;